import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, HardDrive, FileStack, ArrowRight } from 'lucide-react';
import { api, fmtBytes } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { DASHBOARD } from '@/constants/testIds';
import { Skeleton } from '@/components/ui/skeleton';

export default function UserDashboard() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get('/dashboard/user');
                setData(data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading || !data)
        return (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-28 rounded-sm" />
                ))}
            </div>
        );

    return (
        <div data-testid={DASHBOARD.root} className="space-y-8">
            <div>
                <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-1">
                    My Spaces
                </h1>
                <p className="text-sm text-muted-foreground mt-2">
                    Read-only access, {user?.name || user?.email}. You can browse, search, and preview only.
                </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <div className="rounded-sm border border-border bg-card p-5" data-testid={DASHBOARD.metricSpaces}>
                    <div className="text-sm text-muted-foreground flex items-center gap-2"><Database size={14} />My Spaces</div>
                    <div className="mt-3 font-display text-4xl font-black">{data.counts.my_spaces}</div>
                </div>
                <div className="rounded-sm border border-border bg-card p-5" data-testid={DASHBOARD.metricStorage}>
                    <div className="text-sm text-muted-foreground flex items-center gap-2"><HardDrive size={14} />Storage</div>
                    <div className="mt-3 font-display text-4xl font-black">{fmtBytes(data.counts.total_size)}</div>
                </div>
                <div className="rounded-sm border border-border bg-card p-5" data-testid={DASHBOARD.metricFiles}>
                    <div className="text-sm text-muted-foreground flex items-center gap-2"><FileStack size={14} />Files</div>
                    <div className="mt-3 font-display text-4xl font-black">{data.counts.total_files.toLocaleString()}</div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.spaces.length === 0 && (
                    <div className="col-span-full rounded-sm border border-dashed border-border p-10 text-center">
                        <div className="font-display text-xl font-bold">No Spaces assigned yet</div>
                        <p className="text-sm text-muted-foreground mt-2">
                            An administrator will assign Spaces to you. Once assigned, they&apos;ll appear here.
                        </p>
                    </div>
                )}
                {data.spaces.map((s) => (
                    <button
                        key={s.id}
                        data-testid={DASHBOARD.userSpaceCard(s.id)}
                        onClick={() => nav(`/explorer/${s.id}`)}
                        className="text-left rounded-sm border border-border bg-card p-5 hover:border-primary/60 row-hover"
                    >
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="font-display text-lg font-bold tracking-tight">{s.name}</div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {s.region}
                                </div>
                            </div>
                            <ArrowRight size={16} className="text-muted-foreground" />
                        </div>
                        <div className="mt-6 grid grid-cols-3 gap-3 font-mono text-xs">
                            <div>
                                <div className="text-muted-foreground">Size</div>
                                <div className="font-semibold text-sm mt-0.5">{fmtBytes(s.size)}</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Files</div>
                                <div className="font-semibold text-sm mt-0.5">{s.files.toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Folders</div>
                                <div className="font-semibold text-sm mt-0.5">{s.folders}</div>
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}
